/**
 * Itinerary Helper Functions
 *
 * Pure utility functions extracted from WanderAppComplete.jsx for testability.
 * These have zero dependency on React state, refs, or closures.
 */

// ============================================
// TITLE CASE
// ============================================

const ACRONYMS = ['NYC', 'LA', 'SF', 'DC', 'UK', 'USA', 'US', 'EU', 'UAE', 'JFK', 'LAX', 'SFO', 'LHR', 'CDG', 'NRT', 'HND', 'ATL', 'ORD', 'DFW', 'DEN', 'MIA', 'BOS', 'SEA', 'SAN', 'PHX', 'IAH', 'MSP', 'DTW', 'PHL', 'LGA', 'EWR', 'FLL', 'MCO', 'SLC', 'DCA', 'IAD', 'BWI', 'TPA', 'PDX', 'STL', 'BNA', 'AUS', 'RDU', 'MCI', 'SJC', 'SMF', 'OAK', 'SNA', 'DAL', 'HOU', 'MDW', 'BUR', 'ONT', 'PSP', 'CLE', 'CMH', 'IND', 'PIT', 'CVG', 'MKE', 'SAT', 'ABQ', 'OKC', 'TUL', 'OMA', 'DSM', 'MSN', 'GRR', 'SDF', 'BHM', 'JAX', 'RSW', 'PBI', 'TPA', 'MSY', 'MEM', 'BDL', 'PVD', 'PWM', 'ALB', 'BUF', 'SYR', 'ROC', 'SWF', 'HPN', 'ISP', 'ACY', 'TTN', 'ABE', 'AVP', 'MDT', 'ERI', 'IPT', 'SCE', 'JST', 'AOO', 'LBE', 'MGW', 'CKB', 'BKW', 'HTS', 'CRW', 'PKB', 'LWB', 'EKN', 'UKN', 'PSC', 'YVR', 'YYZ', 'YUL', 'YYC', 'YEG', 'YOW', 'YHZ', 'YWG', 'YQB', 'CDG', 'LHR', 'FRA', 'AMS', 'MAD', 'BCN', 'FCO', 'MXP', 'ZRH', 'VIE', 'CPH', 'ARN', 'OSL', 'HEL', 'DUB', 'BRU', 'LIS', 'ATH', 'IST', 'TLV', 'DXB', 'DOH', 'AUH', 'SIN', 'HKG', 'NRT', 'HND', 'ICN', 'PEK', 'PVG', 'CAN', 'BKK', 'KUL', 'CGK', 'MNL', 'DEL', 'BOM', 'SYD', 'MEL', 'BNE', 'PER', 'AKL', 'GRU', 'GIG', 'EZE', 'SCL', 'BOG', 'LIM', 'MEX', 'CUN', 'JNB', 'CPT', 'CAI', 'CMN', 'NBO', 'ADD', 'LOS', 'ACC'];

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX'];

export const toTitleCase = (str) => {
  if (!str) return '';

  return str.replace(/[\w\u00C0-\u024F][\w\u00C0-\u024F'-]*/g, (txt) => {
    const upperTxt = txt.toUpperCase();

    // Check for Roman numerals (preserve if already uppercase)
    if (ROMAN_NUMERALS.includes(upperTxt) && txt === upperTxt) {
      return upperTxt;
    }

    // Only preserve as acronym if the word matches a known acronym AND is already uppercase
    if (ACRONYMS.includes(upperTxt)) {
      const isAlreadyUppercase = txt === upperTxt || (txt.length <= 3 && txt === txt.toUpperCase());
      if (isAlreadyUppercase) {
        return upperTxt;
      }
    }

    // Apply title case — handle hyphens (e.g., "Ritz-Carlton" not "Ritz-carlton")
    return txt.split('-').map(part =>
      part.charAt(0).toUpperCase() + part.substr(1).toLowerCase()
    ).join('-');
  });
};

// ============================================
// STRIP EMOJI
// ============================================

export const stripEmoji = (str) => {
  if (!str) return '';
  return str.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]/gu, '').trim();
};

// ============================================
// PARSE DURATION TO MINUTES
// ============================================

/**
 * Parse duration strings like "2 hr 30 min", "1.5 hours", "45 mins", "3h" into minutes.
 *
 * @param {string} durationStr - Duration string to parse
 * @param {Object} opts - Optional config
 * @param {number} opts.defaultMinutes - Default if unparseable (default: 60)
 * @param {number} opts.minMinutes - Minimum return value (default: 5)
 * @returns {number} Duration in minutes
 */
export const parseDurationToMinutes = (durationStr, opts = {}) => {
  const { defaultMinutes = 60, minMinutes = 5 } = opts;

  if (!durationStr) return defaultMinutes;
  const str = durationStr.toString().toLowerCase();
  let totalMinutes = 0;

  // Match hours component: "2 hr", "1.5 hours", "3h", "2hrs"
  const hourMatch = str.match(/(\d+\.?\d*)\s*(?:hr|hour|h(?:rs)?)\b/);
  if (hourMatch) {
    totalMinutes += parseFloat(hourMatch[1]) * 60;
  }

  // Match minutes component: "30 min", "45 mins", "15m"
  const minMatch = str.match(/(\d+\.?\d*)\s*(?:min|m(?:ins)?)\b/);
  if (minMatch) {
    totalMinutes += parseFloat(minMatch[1]);
  }

  // If no hour/min markers found, try to parse as a plain number
  if (!hourMatch && !minMatch) {
    const num = parseFloat(str);
    if (!isNaN(num)) {
      // If the number is small (< 10), assume hours; otherwise assume minutes
      totalMinutes = num < 10 ? num * 60 : num;
    } else {
      totalMinutes = defaultMinutes; // default fallback
    }
  }

  return Math.max(totalMinutes, minMinutes);
};

// ============================================
// ACTIVITY EMOJI
// ============================================

export const getActivityEmoji = (activityName = '', description = '') => {
  const text = `${activityName} ${description}`.toLowerCase();

  // Landmarks & attractions
  if (text.match(/eiffel tower/i)) return '🗼';
  if (text.match(/tokyo tower/i)) return '🗼';
  if (text.match(/statue|liberty/)) return '🗽';
  if (text.match(/colosseum|coliseum/)) return '🏛️';
  if (text.match(/pyramid/)) return '🏺';
  if (text.match(/great wall/)) return '🧱';
  if (text.match(/taj mahal/)) return '🕌';
  if (text.match(/big ben|parliament/)) return '🏰';
  if (text.match(/tower(?!.*tokyo)/i)) return '🏰';

  // Culture & museums
  if (text.match(/museum|gallery|art/)) return '🎨';
  if (text.match(/palace|castle|chateau/)) return '🏰';
  if (text.match(/cathedral|church|chapel|basilica/)) return '⛪';
  if (text.match(/temple|shrine/)) return '⛩️';
  if (text.match(/mosque/)) return '🕌';
  if (text.match(/opera|theatre|theater|show|concert/)) return '🎭';
  if (text.match(/historic|monument|memorial/)) return '🏛️';

  // Photography (check BEFORE nature/park so "Park Photography" gets 📸)
  if (text.match(/photo|photograph|portrait|camera/)) return '📸';

  // Tea & ceremony (check before generic activities)
  if (text.match(/tea\s*(ceremony|house|experience|garden|room)|matcha|sencha|tea\s+tasting/)) return '🍵';

  // Exploration & walking
  if (text.match(/explore.*street|stroll|wander(?!app)|nearby.*street|neighborhood.*walk|district.*walk/)) return '🏙️';

  // Nature & outdoors
  if (text.match(/beach/)) return '🏖️';
  if (text.match(/mountain|hiking|hike|trek/)) return '⛰️';
  if (text.match(/lake|river/)) return '🌊';
  if (text.match(/park|garden|botanical/)) return '🌳';
  if (text.match(/waterfall/)) return '💦';
  if (text.match(/sunset|sunrise/)) return '🌅';
  if (text.match(/stargazing|star.*gaz|astronomy|observatory/)) return '🌟';
  if (text.match(/safari|wildlife|zoo/)) return '🦁';
  if (text.match(/aquarium|marine/)) return '🐠';

  // Adventure & sports
  if (text.match(/surf/)) return '🏄';
  if (text.match(/ski|snowboard/)) return '⛷️';
  if (text.match(/dive|diving|snorkel/)) return '🤿';
  if (text.match(/kayak|canoe|paddle/)) return '🛶';
  if (text.match(/bike|cycling|bicycle/)) return '🚴';
  if (text.match(/climb|climbing/)) return '🧗';
  if (text.match(/zip ?line/)) return '🎢';
  if (text.match(/paraglid|skydiv/)) return '🪂';
  if (text.match(/golf/)) return '⛳';

  // Entertainment
  if (text.match(/casino/)) return '🎰';
  if (text.match(/club|nightlife|disco/)) return '🪩';
  if (text.match(/spa|massage|wellness/)) return '💆';
  if (text.match(/yoga|meditation/)) return '🧘';
  if (text.match(/pool|swimming/)) return '🏊';

  // Shopping & markets
  if (text.match(/market|bazaar|souk/)) return '🛍️';
  if (text.match(/shop|mall|boutique/)) return '🛒';
  if (text.match(/wine|vineyard|winery/)) return '🍷';
  if (text.match(/brewery|beer/)) return '🍺';

  // Tours & experiences
  if (text.match(/boat|cruise|ferry|sailing/)) return '⛵';
  if (text.match(/helicopter/)) return '🚁';
  if (text.match(/hot air balloon/)) return '🎈';
  if (text.match(/cooking class/)) return '👨‍🍳';
  if (text.match(/walking tour|food tour/)) return '🚶';

  // Transport related
  if (text.match(/check.?in|hotel.*arrival/i)) return '🛎️';
  if (text.match(/check.?out/i)) return '🧳';
  if (text.match(/airport|flight|departure/)) return '✈️';
  if (text.match(/train|rail/)) return '🚂';
  if (text.match(/car.*rental|rent.*car/)) return '🚗';

  // Transit / arrival / day-close (C5 — reported missing emoji on cards)
  if (text.match(/transit to|transit\s*$|drive to/i)) return '🚕';
  if (text.match(/arrival.*(customs|immigration)|customs|immigration|passport control/i)) return '🛂';
  if (text.match(/baggage|bag claim/i)) return '🧳';
  if (text.match(/end of day|rest at|freshen up|hotel amenities/i)) return '🌙';
  if (text.match(/return to hotel|back to hotel|home return/i)) return '🏠';
  if (text.match(/free time/i)) return '✨';

  // Default
  return '✨';
};

// ============================================
// TYPE → EMOJI (complements name-based matcher)
// ============================================
// Use as a second-pass when name-based getActivityEmoji returns the default
// '✨' or the item has no activity string. Keyed on the engine's canonical
// `type` field. Added for C5 — ensures every card has a usable emoji even
// when the LLM omits one in the label.

export const getTypeEmoji = (type = '') => {
  switch (type) {
    case 'arrival-customs':
    case 'arrival-immigration':
      return '🛂';
    case 'hotel-checkin':
      return '🛎️';
    case 'hotel-checkout':
      return '🧳';
    case 'airport-transit':
    case 'station-transit':
    case 'port-transit':
      return '🚕';
    case 'transit':
    case 'drive':
      return '🚕';
    case 'long-distance-transfer':
      return '🚗';
    case 'inter-city-travel':
    case 'flight':
    case 'overnight-journey':
    case 'overnight-arrival':
      return '✈️';
    case 'end-of-day':
    case 'home-return':
      return '🏠';
    case 'recovery-note':
      return '🌙';
    case 'free-time':
      return '✨';
    case 'meal':
      return '🍽️';
    case 'activity':
      return '✨';
    default:
      return null;
  }
};

// ============================================
// MEAL EMOJI
// ============================================

export const getMealEmoji = (restaurant = '', cuisine = '', mealType = '') => {
  const text = `${restaurant} ${cuisine} ${mealType}`.toLowerCase();

  // Specific cuisines
  if (text.match(/italian|pizza|pasta|trattoria|ristorante/)) return '🍝';
  if (text.match(/sushi|japanese|ramen|izakaya/)) return '🍣';
  if (text.match(/chinese|dim sum|dumpling/)) return '🥟';
  if (text.match(/thai/)) return '🍜';
  if (text.match(/indian|curry/)) return '🍛';
  if (text.match(/mexican|taco|burrito/)) return '🌮';
  if (text.match(/french|bistro|brasserie/)) return '🥐';
  if (text.match(/spanish|tapas|paella/)) return '🥘';
  if (text.match(/greek|mediterranean/)) return '🫒';
  if (text.match(/korean|bbq|bulgogi/)) return '🥩';
  if (text.match(/vietnamese|pho|banh mi/)) return '🍜';
  if (text.match(/middle eastern|lebanese|falafel|hummus/)) return '🧆';
  if (text.match(/american|burger|bbq/)) return '🍔';

  // Food types
  if (text.match(/seafood|fish|oyster|lobster|crab|shrimp/)) return '🦞';
  if (text.match(/steak|steakhouse|grill|meat/)) return '🥩';
  if (text.match(/vegetarian|vegan|plant|salad/)) return '🥗';
  if (text.match(/bakery|pastry|croissant/)) return '🥐';
  if (text.match(/ice cream|gelato/)) return '🍦';
  if (text.match(/dessert|sweet|chocolate/)) return '🍰';
  if (text.match(/alpine|mountain|fondue|raclette|schnitzel|chalet|hütte|lodge.*restaurant/)) return '🏔️';
  if (text.match(/swiss|swiss-german|rösti/)) return '🫕';

  // Meal types & drinks
  if (text.match(/tea\s*(ceremony|house|experience)|matcha|sencha|tea\s+tasting/)) return '🍵';
  if (text.match(/breakfast|brunch/)) return '🍳';
  if (text.match(/coffee|cafe|café/)) return '☕';
  if (text.match(/wine bar|wine/)) return '🍷';
  if (text.match(/cocktail|bar/)) return '🍸';
  if (text.match(/pub|beer/)) return '🍺';

  // Default by meal type
  if (mealType.toLowerCase() === 'breakfast') return '🍳';
  if (mealType.toLowerCase() === 'lunch') return '🍽️';
  if (mealType.toLowerCase() === 'dinner') return '🍷';

  return '🍴';
};

// ============================================
// CATEGORIZE ACTIVITY
// ============================================

export const categorizeActivity = (activityName, description = '') => {
  const text = `${activityName} ${description}`.toLowerCase();

  if (text.match(/museum|palace|castle|cathedral|temple|church|historic|monument|ruins/)) return 'culture';
  if (text.match(/hike|climb|kayak|surf|ski|bike|adventure|trek|zip|paraglid/)) return 'adventure';
  if (text.match(/restaurant|cafe|food|dining|lunch|dinner|breakfast|brunch|market|cuisine/)) return 'food';
  if (text.match(/spa|massage|yoga|meditation|beach|relax|pool|hammam|sauna/)) return 'relaxation';
  if (text.match(/shop|mall|market|boutique|store|souvenir/)) return 'shopping';
  if (text.match(/park|garden|nature|forest|lake|mountain|waterfall|wildlife|botanical/)) return 'nature';
  if (text.match(/bar|club|nightlife|pub|lounge|disco|concert|show/)) return 'nightlife';
  if (text.match(/gallery|art|exhibit|paint|sculpture|craft/)) return 'art';

  return 'culture'; // Default
};
